import { useEffect, useState } from 'react';
import { getStrataManagers, getUnitStrataDetails } from 'src/services/client';
import {
  IGroupedUnitAssignments,
  IStrataManager,
  IUnitStrataAssignments,
} from 'src/pages/Property/ManageStrataManagers/types';
import { groupUnitsByCommonArea } from 'src/pages/Property/ManageStrataManagers/helpers';

export interface UseStrataAssignmentsDataReturn {
  commonAreas: IUnitStrataAssignments[];
  generalUnits: IUnitStrataAssignments[];
  availableManagers: IStrataManager[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const unwrapList = async (res: any): Promise<any[]> => {
  if (res?.isError || !res?.ok) {
    throw new Error(res?.error ?? res?.message ?? 'Request failed');
  }

  const json: any = await res.json();

  return json?.data ?? json;
};

const normalizeStrataManager = (manager: any): IStrataManager => ({
  userId: manager.userId,
  firstName: manager.firstName,
  lastName: manager.lastName,
  email: manager.email,
  profileImage: manager.profileImage ?? null,
});

// legacy/workaround: the API returns the unit label as `number` and omits building,
// so map the response onto the UI's { unitNumber, building } shape.
const normalizeUnitStrataDetails = (unit: any): IUnitStrataAssignments => ({
  unitId: unit.unitId,
  unitNumber: unit.number,
  building: unit.building ?? null,
  isCommonArea: unit.isCommonArea ?? false,
  assignedStrataManagers: (unit.assignedStrataManagers ?? []).map(
    (manager: any) => normalizeStrataManager(manager)
  ),
});

export const useStrataAssignmentsData = (
  propertyId: string
): UseStrataAssignmentsDataReturn => {
  const [unitAssignments, setUnitAssignments] = useState<
    IUnitStrataAssignments[]
  >([]);
  const [availableManagers, setAvailableManagers] = useState<IStrataManager[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const [assignmentsRes, managersRes] = await Promise.all([
        getUnitStrataDetails(propertyId),
        getStrataManagers(),
      ]);

      const rawAssignments: any[] = await unwrapList(assignmentsRes);
      const rawManagers: any[] = await unwrapList(managersRes);

      const assignments: IUnitStrataAssignments[] = rawAssignments.map(
        (unit: any) => normalizeUnitStrataDetails(unit)
      );

      const managers: IStrataManager[] = rawManagers.map((manager: any) =>
        normalizeStrataManager(manager)
      );

      setUnitAssignments(assignments);
      setAvailableManagers(managers);
    } catch (err) {
      console.error('Failed to load strata assignments:', err);
      setError('Could not load unit assignments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    void fetchData();
  }, [propertyId]);

  const { commonAreas, generalUnits }: IGroupedUnitAssignments =
    groupUnitsByCommonArea(unitAssignments);

  return {
    commonAreas,
    generalUnits,
    availableManagers,
    isLoading,
    error,
    refetch: fetchData,
  };
};